import type { Collection } from "@/api/client";
import { formatDistanceToNow } from "@/lib/utils";
import { Link } from "wouter";
import { ArrowRight, Folder, Trash2 } from "lucide-react";

interface CollectionCardProps {
  collection: Collection;
  onDelete?: (id: string) => void;
}

export function CollectionCard({ collection, onDelete }: CollectionCardProps) {
  return (
    <div className="group relative bg-card rounded-xl border border-border overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-violet-500/30">
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500/20 to-violet-500/10 flex items-center justify-center">
              <Folder className="w-4 h-4 text-violet-500" />
            </div>
            <h3 className="font-semibold text-lg text-foreground truncate">
              {collection.name}
            </h3>
          </div>
          <span className="px-2 py-1 text-xs font-medium bg-violet-500/10 text-violet-500 rounded-full">
            {collection.itemCount ?? 0} 项
          </span>
        </div>
        
        {collection.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {collection.description}
          </p>
        )}
        
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{formatDistanceToNow(new Date(collection.createdAt))}</span>
        </div>
      </div>
      
      {/* Footer */}
      <div className="px-5 py-3 bg-muted/50 border-t border-border flex items-center justify-between">
        <Link
          href={`/collections/${collection.id}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-500 hover:underline"
        >
          查看合集
          <ArrowRight className="w-4 h-4" />
        </Link>
        
        {onDelete && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onDelete(collection.id);
            }}
            className="p-2 text-muted-foreground hover:text-destructive rounded-lg transition-colors opacity-0 group-hover:opacity-100"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
